/**
 * Generic AU address formatter that formats the address to the format 123 Main St Unit 4 Sydney NSW 2000
 * @param address1 - The first line of the address.
 * @param city - The city of the address.
 * @param postal - The postal code of the address.
 * @param state - The state of the address.
 * @param address2 - The second line of the address.
 * @returns The formatted address.
 */
export const formatAddress = (
  address1: string,
  city: string,
  postal: string,
  state: string,
  address2?: string,
) =>
  [address1, address2, city, state, postal]
    .filter((part) => part && part.trim().length > 0)
    .join(' ');

const AU_LANDLINE_AREA_CODES = ['02', '03', '07', '08'];

// +61 is only valid for mobiles (04XX) and landlines (02/03/07/08).
// Service numbers (13/1300/1800) are domestic-only and never use +61.
// Numbers arrive as 11 digits (e.g. 61412345678) or 12 when the redundant
// trunk prefix 0 is included (e.g. 610412345678). Strip the country code
// so downstream formatting works with 10-digit local numbers.
const normaliseAuInternationalPrefix = (digits: string): string => {
  if (digits.length === 11 && digits.startsWith('61')) {
    return `0${digits.slice(2)}`;
  }
  if (digits.length === 12 && digits.startsWith('610')) {
    return digits.slice(2);
  }
  return digits;
};

/**
 * Formats Australian phone numbers into their conventional display format.
 *
 * Supported formats:
 *  - Mobile (04XX):          0412 345 678
 *  - Landline (02/03/07/08): (02) 1234 5678
 *  - +61 international:      normalised to local then formatted
 *  - 1300 / 1800:            1300 123 456 / 1800 123 456
 *  - 13 (short):             13 12 34
 *
 * Returns the original string unchanged if it doesn't match a known AU pattern.
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  const normalised = normaliseAuInternationalPrefix(digitsOnly);

  if (normalised.length === 10) {
    if (normalised.startsWith('04')) {
      return normalised.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
    }

    if (AU_LANDLINE_AREA_CODES.some((code) => normalised.startsWith(code))) {
      return normalised.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2 $3');
    }

    if (normalised.startsWith('1300') || normalised.startsWith('1800')) {
      return normalised.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
    }
  }

  if (normalised.length === 6 && normalised.startsWith('13')) {
    return normalised.replace(/(\d{2})(\d{2})(\d{2})/, '$1 $2 $3');
  }

  return phoneNumber;
};
